import { useState, useEffect } from 'react';
import { MEDIA_ATTRIBUTION } from '../../engine/mediaLibrary';
import styles from './MediaOverlay.module.css';

/**
 * Fullscreen diagnostic media popup shown on the monitor (learner) screen.
 *
 * Renders when `media` is non-null. Displays an X-ray/photo (image) or a
 * POCUS clip (video, autoplay + loop). The instructor triggers/dismisses it
 * remotely via synced store state; a close button is also provided here so
 * the media can be dismissed from either screen.
 *
 * @param {{ media: {id,type,src,label}|null, onClose: () => void }} props
 */
export default function MediaOverlay({ media, onClose }) {
  const [failed, setFailed] = useState(false);

  // Reset the load-error flag whenever a different media item is shown
  useEffect(() => setFailed(false), [media?.id]);

  if (!media) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.frame} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.caption}>{media.label}</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {failed ? (
          <div className={styles.missing}>
            Media file not found
            <span className={styles.missingPath}>{media.src}</span>
          </div>
        ) : media.type === 'video' ? (
          <video
            className={styles.media}
            src={media.src}
            autoPlay
            loop
            controls
            playsInline
            onError={() => setFailed(true)}
          />
        ) : (
          <img
            className={styles.media}
            src={media.src}
            alt={media.label}
            onError={() => setFailed(true)}
          />
        )}

        {!failed && <span className={styles.attribution}>{MEDIA_ATTRIBUTION}</span>}
      </div>
    </div>
  );
}
